import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useLocation, useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Nav, Tab, Spinner } from "react-bootstrap";
import { FiArrowLeft } from "react-icons/fi";
import { getApp } from "../../services/integration.service";
import { ToastrContext } from "../../provider/toastrContext";
import OverviewTab from "./component/overviewTab";
import CredentialsTab from "./component/credentialsTab";
import WebhooksTab from "./component/webhooksTab";
import EventsTab from "./component/eventsTab";

const VALID_TABS = ["overview", "credentials", "webhooks", "events"];

const hashToTab = (hash) => {
  const stripped = (hash || "").replace(/^#/, "");
  return VALID_TABS.includes(stripped) ? stripped : "overview";
};

const DevelopersDetail = () => {
  const { appPubID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showError } = useContext(ToastrContext);
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const activeTab = hashToTab(location.hash);

  const handleSelect = (k) => {
    if (!k || !VALID_TABS.includes(k)) return;
    navigate(`#${k}`, { replace: false });
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getApp(appPubID);
      setApp(res);
    } catch (e) {
      setApp(null);
      setError(e);
      showError((e && e.message) || "Failed to load app");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appPubID]);

  return (
    <div className="container-fluid p-0">
      <Helmet>
        <title>{app ? `${app.name} — Developers` : "Developers"} — Otokata</title>
      </Helmet>
      <div className="row">
        <div className="col-12">
          <div className="mb-3">
            <Link to="/developers" className="text-muted">
              <FiArrowLeft className="align-middle me-1" />
              Back to apps
            </Link>
          </div>
          <div className="card">
            {loading && !app ? (
              <div className="card-body text-center p-5">
                <Spinner animation="border" />
              </div>
            ) : !app ? (
              <div className="card-body text-center p-5">
                <h5>App not found</h5>
                <p className="text-muted mb-0">
                  {(error && error.message) ||
                    "The app could not be loaded. It may have been revoked or belong to another workspace."}
                </p>
              </div>
            ) : (
              <Tab.Container activeKey={activeTab} onSelect={handleSelect}>
                <div className="card-header">
                  <Nav variant="tabs">
                    <Nav.Item>
                      <Nav.Link eventKey="overview">Overview</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="credentials">Credentials</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="webhooks">Webhooks</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="events">Events</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </div>
                <div className="card-body">
                  <Tab.Content>
                    <Tab.Pane eventKey="overview">
                      <OverviewTab app={app} onUpdated={setApp} />
                    </Tab.Pane>
                    <Tab.Pane eventKey="credentials" mountOnEnter>
                      <CredentialsTab appPubID={appPubID} />
                    </Tab.Pane>
                    <Tab.Pane eventKey="webhooks" mountOnEnter>
                      <WebhooksTab appPubID={appPubID} />
                    </Tab.Pane>
                    <Tab.Pane eventKey="events" mountOnEnter>
                      <EventsTab appPubID={appPubID} />
                    </Tab.Pane>
                  </Tab.Content>
                </div>
              </Tab.Container>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevelopersDetail;
